import Vue from 'vue'

class PaymentMethodRepository {

  // POST /api/payment-methods/sync
  // Body: {organizationId: "xxx"} OU {customerId: "xxx"}
  syncPaymentMethods({ organizationId, customerId }) {
    return new Promise((resolve, reject) => {
      const data = {}
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment-methods/sync`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /api/payment-methods/search
  // Body: {organizationId: "xxx", limit, offset, type, status, statuses} OU {customerId: "xxx", ...}
  listPaymentMethods({ organizationId, customerId, limit = 10, offset = 0, type, status, statuses, forceSync } = {}) {
    return new Promise((resolve, reject) => {
      const data = { limit, offset }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (type) data.type = type
      if (status) data.status = status
      if (statuses && Array.isArray(statuses) && statuses.length > 0) {
        data.statuses = statuses
      }
      if (forceSync) data.forceSync = true

      Vue.auth.fetch({
        url: `/api/payment-methods/search`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /api/payment-methods
  // Body: {organizationId: "xxx", paymentMethodId: "pm_xxx"} OU {customerId: "xxx", paymentMethodId: "pm_xxx"}
  addPaymentMethod({ organizationId, customerId, paymentMethodId, returnUrl }) {
    return new Promise((resolve, reject) => {
      const data = { paymentMethodId }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (returnUrl) data.returnUrl = returnUrl

      Vue.auth.fetch({
        url: `/api/payment-methods`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // PATCH /api/payment-methods/{setupIntentId}
  // Body: {organizationId: "xxx", name: "Ma carte"} OU {customerId: "xxx", name: "Ma carte"}
  updatePaymentMethod(setupIntentId, { organizationId, customerId, name }) {
    return new Promise((resolve, reject) => {
      const data = { name }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment-methods/${setupIntentId}`,
        method: 'patch',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // DELETE /api/payment-methods/{setupIntentId}
  // Body: {organizationId: "xxx"} OU {customerId: "xxx"}
  deletePaymentMethod(paymentMethodId, { organizationId, customerId }) {
    return new Promise((resolve, reject) => {
      const data = {}
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment-methods/${paymentMethodId}`,
        method: 'delete',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /api/payment-methods/setup-intent
  // Body: {organizationId: "xxx", paymentMethodType: "card"} OU {customerId: "xxx", paymentMethodType: "card"}
  createSetupIntent({ organizationId, customerId, tokenId, paymentMethodId, paymentMethodType, returnUrl } = {}) {
    return new Promise((resolve, reject) => {
      const data = {}
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (tokenId) data.tokenId = tokenId
      if (paymentMethodId) data.paymentMethodId = paymentMethodId
      if (paymentMethodType) data.paymentMethodType = paymentMethodType
      if (returnUrl) data.returnUrl = returnUrl

      Vue.auth.fetch({
        url: `/api/payment-methods/setup-intent`,
        method: 'post',
        data: Object.keys(data).length > 0 ? data : undefined,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /api/payment-methods/setup-intent/{setupIntentId}/status
  // Body: {organizationId: "xxx"} OU {customerId: "xxx"}
  getSetupIntentStatus(setupIntentId, { organizationId, customerId } = {}) {
    return new Promise((resolve, reject) => {
      const data = { setupIntentId }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment-methods/setup-intent/${setupIntentId}/status`,
        method: 'post',
        data,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /api/payments
  // Body: {organizationId: "xxx", paymentMethodId, amount, currency, ...} OU {customerId: "xxx", ...}
  createPayment({ organizationId, customerId, paymentMethodId, amount, currency, metadata, capture, returnUrl }) {
    return new Promise((resolve, reject) => {
      const data = { paymentMethodId, amount, currency, metadata, capture }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (returnUrl) data.returnUrl = returnUrl

      Vue.auth.fetch({
        url: `/api/payments`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // GET /payments/{paymentIntentId}
  // Récupère un PaymentIntent (vérifie automatiquement les permissions côté backend)
  getPaymentIntent(paymentIntentId) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/payments/${paymentIntentId}`,
        method: 'get',
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payments/{paymentIntentId}/generate-auth-url
  // Force la génération d'une URL de redirection pour l'authentification
  generateAuthUrl(paymentIntentId, { returnUrl }) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/payments/${paymentIntentId}/generate-auth-url`,
        method: 'post',
        data: { returnUrl }
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // GET /payment/inbound/sepa/mandates/{pmId}/prepare-view
  prepareMandateView(paymentMethodId, { organizationId, customerId, signatoryFirstName, signatoryLastName, locale = 'fr' } = {}) {
    return new Promise((resolve, reject) => {
      const data = { locale }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (signatoryFirstName) data.signatoryFirstName = signatoryFirstName
      if (signatoryLastName) data.signatoryLastName = signatoryLastName

      Vue.auth.fetch({
        url: `/api/payment/inbound/sepa/mandates/${paymentMethodId}/prepare-view`,
        method: 'get',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // GET /payment/inbound/sepa/mandates/{pmId}/signed-view
  getSignedMandateView(paymentMethodId, { organizationId, customerId, locale = 'fr' } = {}) {
    return new Promise((resolve, reject) => {
      const data = { locale }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment/inbound/sepa/mandates/${paymentMethodId}/signed-view`,
        method: 'get',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/inbound/sepa/mandates/accept
  acceptMandate({ contentHash, snapshotData, paymentMethodId, clientIp, userAgent, organizationId, customerId }) {
    return new Promise((resolve, reject) => {
      const data = {
        paymentMethodId,
        contentHash,
        snapshotData,
        clientIp,
        userAgent
      }
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId

      Vue.auth.fetch({
        url: `/api/payment/inbound/sepa/mandates/accept`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // GET /mandates/{mandateId}/pdf-url
  getMandatePdfUrl(mandateId) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/mandates/${mandateId}/pdf-url`,
        method: 'get',
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/transactions/search (page token pagination)
  getTransactions({ paymentMethodId, beneficiaryId, organizationId, customerId, status, transactionType, limit = 20, page = null } = {}) {
    return new Promise((resolve, reject) => {
      const data = {
        limit
      }

      // Add page token if provided
      if (page) data.page = page

      // Add filters
      if (paymentMethodId) data.paymentMethodId = paymentMethodId
      if (beneficiaryId) data.beneficiaryId = beneficiaryId
      if (organizationId) data.organizationId = organizationId
      if (customerId) data.customerId = customerId
      if (status) data.status = status
      if (transactionType) data.transactionType = transactionType

      Vue.auth.fetch({
        url: `/api/payment/transactions/search`,
        method: 'post',
        data,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/transactions/{paymentIntentId}/cancel
  cancelPayment(paymentIntentId, { cancellation_reason } = {}) {
    return new Promise((resolve, reject) => {
      const data = {}
      if (cancellation_reason) data.cancellation_reason = cancellation_reason

      Vue.auth.fetch({
        url: `/api/payment/transactions/${paymentIntentId}/cancel`,
        method: 'post',
        data: Object.keys(data).length > 0 ? data : undefined,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/transactions/{paymentIntentId}/capture
  capturePayment(paymentIntentId, { amount_to_capture, final_capture = true } = {}) {
    return new Promise((resolve, reject) => {
      const data = { final_capture }
      if (amount_to_capture) data.amount_to_capture = amount_to_capture

      Vue.auth.fetch({
        url: `/api/payment/transactions/${paymentIntentId}/capture`,
        method: 'post',
        data,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/transactions/{paymentIntentId}/refund
  refundPayment(paymentIntentId, { amount, reason, metadata } = {}) {
    return new Promise((resolve, reject) => {
      const data = {}
      if (amount) data.amount = amount
      if (reason) data.reason = reason
      if (metadata) data.metadata = metadata

      Vue.auth.fetch({
        url: `/api/payment/transactions/${paymentIntentId}/refund`,
        method: 'post',
        data: Object.keys(data).length > 0 ? data : undefined,
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/payouts/{payoutId}/cancel
  cancelPayout(payoutId) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/payment/payouts/${payoutId}/cancel`,
        method: 'post',
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }

  // POST /payment/transfers/{transferId}/reverse
  reverseTransfer(transferId, data = {}) {
    return new Promise((resolve, reject) => {
      Vue.auth.fetch({
        url: `/api/payment/transfers/${transferId}/reverse`,
        method: 'post',
        data
      }).then(response => {
        resolve(response.data)
      }).catch(err => reject(err))
    })
  }
}

export default new PaymentMethodRepository()
